import { useState } from 'react';
import { api, ApiError, formatDate, formatDateTime } from '../../../lib/api';
import { useAuth } from '../../../lib/auth';
import { loadList, upsertList } from '../../../lib/localList';
import type { DvSupportCase, DvSupportCaseDetail, SafetyPlanRevision, ShelterPlacement } from '../../../lib/types';
import { useApiQuery } from '../../../lib/useApi';
import { EmptyState, ErrorNote, Field, PageHeader, Spinner, StatusBadge } from '../../../ui/components';

// Same gate as case management proper — DvSupportCase access is entirely
// delegated to the parent SocialCase's FULL access (requireFullCaseAccess),
// see m27-family-affairs/src/routes/dv.ts's own header comment. There is no
// separate "DV role" — a DV-category case is worked by the same
// SOCIAL_WORKER/SENIOR_SW who would work any other case category.
const STAFF_ROLES = ['SOCIAL_WORKER', 'SENIOR_SW'];

/**
 * DV support: safety plans + shelter placements — GET/POST
 * /api/v1/m27/dv-cases/:id, POST /dv-cases, POST .../safety-plan,
 * POST .../shelter-placements(+/:pid/end).
 *
 * Backend gap flagged in this app's build report: there is no
 * `GET /dv-cases` list and no `GET /dv-cases?caseId=` lookup — a DV support
 * case can only be reached by its own id, or by re-POSTing `{ caseId }`
 * against its parent SocialCase (which the backend treats as idempotent:
 * a second POST 409s with the existing id in `error.details.dvSupportCaseId`
 * — real, documented behaviour this UI relies on, not a workaround). This
 * mirrors the exact situation this app's own src/lib/localList.ts already
 * documents for S04/S05/S06/S08 (write-heavy routers with no list
 * endpoint): a local, per-persona cache of DV cases opened or looked up
 * this session stands in for a browse screen.
 */
export function DvSupport() {
  const { persona } = useAuth();
  const isStaff = persona?.roles.some((r) => STAFF_ROLES.includes(r)) ?? false;

  const [cached, setCached] = useState<DvSupportCase[]>(() => loadList<DvSupportCase>('familyAffairs.dvCases'));
  const [openId, setOpenId] = useState<string | null>(null);
  const [dvIdInput, setDvIdInput] = useState('');
  const [caseIdInput, setCaseIdInput] = useState('');
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [lookupBusy, setLookupBusy] = useState(false);

  if (!isStaff) {
    return (
      <div>
        <PageHeader title="DV Support" />
        <ErrorNote message="This view requires the Social Worker or Senior Social Worker role." />
      </div>
    );
  }

  const openByDvId = async (id: string) => {
    if (!id) return;
    setLookupBusy(true);
    setLookupError(null);
    try {
      const dv = await api.get<DvSupportCaseDetail>(`/api/v1/m27/dv-cases/${id}`);
      setCached(upsertList('familyAffairs.dvCases', dv));
      setOpenId(dv.id);
    } catch (e) {
      setLookupError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not open DV support case');
    } finally {
      setLookupBusy(false);
    }
  };

  const openOrCreateByCaseId = async (caseId: string) => {
    if (!caseId) return;
    setLookupBusy(true);
    setLookupError(null);
    try {
      const dvId = await openOrCreateDvSupportCase(caseId);
      await openByDvId(dvId);
    } catch (e) {
      setLookupError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not open DV support case for that social case');
    } finally {
      setLookupBusy(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="DV Support — Safety Plans & Shelter Placements"
        subtitle="Every DV support case is opened against an existing DV-category social case (see Case Register)"
      />
      {lookupError ? (
        <div className="mb-4">
          <ErrorNote message={lookupError} />
        </div>
      ) : null}

      <div className="mb-6 grid gap-3 sm:grid-cols-2">
        <div className="card p-4">
          <div className="field-label mb-2">Open by DV support case ID</div>
          <div className="flex gap-2">
            <input className="field-input" value={dvIdInput} onChange={(e) => setDvIdInput(e.target.value)} placeholder="cl…" />
            <button className="btn-secondary shrink-0" onClick={() => openByDvId(dvIdInput)} disabled={lookupBusy || !dvIdInput}>
              Open
            </button>
          </div>
        </div>
        <div className="card p-4">
          <div className="field-label mb-2">Open (or start) by parent social case ID</div>
          <div className="flex gap-2">
            <input className="field-input" value={caseIdInput} onChange={(e) => setCaseIdInput(e.target.value)} placeholder="cl… (category must be DV)" />
            <button className="btn-primary shrink-0" onClick={() => openOrCreateByCaseId(caseIdInput)} disabled={lookupBusy || !caseIdInput}>
              Open / start
            </button>
          </div>
        </div>
      </div>

      <div>
        <h2 className="mb-2 text-sm font-bold text-slate-700">DV support cases opened this session</h2>
        <div className="card overflow-x-auto">
          {cached.length === 0 ? (
            <EmptyState title="None yet" hint="Use one of the lookups above — there is no browse-all endpoint for DV support cases." />
          ) : (
            <table className="w-full">
              <thead>
                <tr>
                  <th className="table-head">Ref</th>
                  <th className="table-head">Survivor</th>
                  <th className="table-head">District</th>
                  <th className="table-head" />
                </tr>
              </thead>
              <tbody>
                {cached.map((d) => (
                  <tr key={d.id} className="hover:bg-slate-50">
                    <td className="table-cell font-medium">{d.refNo}</td>
                    <td className="table-cell text-xs">{d.survivorName}</td>
                    <td className="table-cell text-xs">{d.district.replaceAll('-', ' ')}</td>
                    <td className="table-cell text-right">
                      <button className="btn-secondary !px-2.5 !py-1 text-xs" onClick={() => openByDvId(d.id)}>
                        Open
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {openId ? (
        <DvCaseDetail
          id={openId}
          onClose={() => setOpenId(null)}
          onUpdated={(dv) => setCached(upsertList('familyAffairs.dvCases', dv))}
        />
      ) : null}
    </div>
  );
}

/**
 * `POST /dv-cases` is a genuine get-or-create: a second call against the
 * same parent caseId 409s with the existing row's id in
 * `error.details.dvSupportCaseId` (see routes/dv.ts) — real backend
 * behaviour, not a client-side workaround.
 */
async function openOrCreateDvSupportCase(caseId: string): Promise<string> {
  try {
    const created = await api.post<DvSupportCase>('/api/v1/m27/dv-cases', { caseId });
    return created.id;
  } catch (e) {
    if (e instanceof ApiError && e.status === 409) {
      const details = e.details as { dvSupportCaseId?: string } | undefined;
      if (details?.dvSupportCaseId) return details.dvSupportCaseId;
    }
    throw e;
  }
}

/**
 * Full DV support case detail: fetches fresh, then renders the reusable
 * `DvCasePanel` below — exported so Case Register can embed the exact same
 * safety-plan/shelter UI for a DV-category case without duplicating it.
 */
function DvCaseDetail({ id, onClose, onUpdated }: { id: string; onClose: () => void; onUpdated: (dv: DvSupportCase) => void }) {
  const dvQ = useApiQuery<DvSupportCaseDetail>(`/api/v1/m27/dv-cases/${id}`);
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-ink-950/50 p-4 pt-[6vh]" onClick={onClose}>
      <div className="card w-full max-w-2xl p-5" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-base font-bold text-slate-900">{dvQ.data ? `DV support — ${dvQ.data.refNo}` : 'DV support case'}</h2>
          <button className="rounded-md px-2 py-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
        {dvQ.loading ? (
          <Spinner />
        ) : dvQ.error ? (
          <ErrorNote message={dvQ.error} />
        ) : dvQ.data ? (
          <DvCasePanel
            dvCase={dvQ.data}
            onReload={() => {
              dvQ.reload();
              onUpdated(dvQ.data!);
            }}
          />
        ) : null}
      </div>
    </div>
  );
}

/**
 * Reusable panel given an already-loaded DvSupportCaseDetail — imported
 * directly by CaseRegister.tsx's case detail view for DV-category cases, so
 * a caseworker never has to leave the case file to check or update a
 * safety plan.
 */
export function DvCasePanel({ dvCase, onReload }: { dvCase: DvSupportCaseDetail; onReload: () => void }) {
  return (
    <div className="space-y-5">
      <div className="text-xs text-slate-500">
        Survivor {dvCase.survivorName} ({dvCase.survivorNin}) · District {dvCase.district.replaceAll('-', ' ')}
      </div>
      <SafetyPlanSection dvSupportCaseId={dvCase.id} revisions={dvCase.safetyPlanRevisions} onReload={onReload} />
      <ShelterSection dvSupportCaseId={dvCase.id} placements={dvCase.shelterPlacements} onReload={onReload} />
    </div>
  );
}

function SafetyPlanSection({
  dvSupportCaseId,
  revisions,
  onReload,
}: {
  dvSupportCaseId: string;
  revisions: SafetyPlanRevision[];
  onReload: () => void;
}) {
  const [showAdd, setShowAdd] = useState(false);
  const [steps, setSteps] = useState('');
  const [contacts, setContacts] = useState('');
  const [reviewedAt, setReviewedAt] = useState(() => new Date().toISOString().slice(0, 10));
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const latest = revisions[0];

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m27/dv-cases/${dvSupportCaseId}/safety-plan`, {
        planJson: {
          steps: steps.split('\n').map((s) => s.trim()).filter(Boolean),
          emergencyContacts: contacts.split('\n').map((s) => s.trim()).filter(Boolean),
        },
        reviewedAt: reviewedAt || undefined,
      });
      setSteps('');
      setContacts('');
      setShowAdd(false);
      onReload();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not record safety plan revision');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-sm font-bold text-slate-700">Safety plan {latest ? `— version ${latest.version}` : '(none recorded)'}</h3>
        <button className="btn-secondary !px-2.5 !py-1 text-xs" onClick={() => setShowAdd((v) => !v)}>
          {showAdd ? 'Cancel' : '+ New revision'}
        </button>
      </div>
      {error ? (
        <div className="mb-2">
          <ErrorNote message={error} />
        </div>
      ) : null}
      {latest ? (
        <div className="mb-3 rounded-lg border border-slate-200 p-3 text-sm">
          <div className="field-label">Steps</div>
          <ul className="mb-2 list-inside list-disc text-slate-700">
            {(latest.planJson.steps ?? []).map((s, i) => (
              <li key={i}>{s}</li>
            ))}
          </ul>
          <div className="field-label">Emergency contacts</div>
          <ul className="list-inside list-disc text-slate-700">
            {(latest.planJson.emergencyContacts ?? []).map((c, i) => (
              <li key={i}>{c}</li>
            ))}
          </ul>
          <div className="mt-2 text-xs text-slate-400">
            Authorised by {latest.authorisedBy} · {latest.reviewedAt ? `reviewed ${formatDate(latest.reviewedAt)}` : 'not yet reviewed'}
          </div>
        </div>
      ) : null}
      {revisions.length > 1 ? (
        <details className="mb-3 text-xs text-slate-500">
          <summary className="cursor-pointer font-medium">{revisions.length - 1} earlier revision(s)</summary>
          <ul className="mt-1 space-y-1">
            {revisions.slice(1).map((r) => (
              <li key={r.id}>
                v{r.version} — {formatDateTime(r.createdAt)} by {r.authorisedBy}
              </li>
            ))}
          </ul>
        </details>
      ) : null}
      {showAdd ? (
        <div className="space-y-2 rounded-lg border border-slate-200 p-3">
          <Field label="Steps (one per line)">
            <textarea className="field-input" rows={3} value={steps} onChange={(e) => setSteps(e.target.value)} />
          </Field>
          <Field label="Emergency contacts (one per line)">
            <textarea className="field-input" rows={2} value={contacts} onChange={(e) => setContacts(e.target.value)} />
          </Field>
          <Field label="Reviewed on">
            <input className="field-input" type="date" value={reviewedAt} onChange={(e) => setReviewedAt(e.target.value)} />
          </Field>
          <div className="flex justify-end">
            <button className="btn-primary !py-1 text-xs" onClick={submit} disabled={busy || !steps.trim()}>
              {busy ? 'Saving…' : 'Save revision'}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function ShelterSection({
  dvSupportCaseId,
  placements,
  onReload,
}: {
  dvSupportCaseId: string;
  placements: ShelterPlacement[];
  onReload: () => void;
}) {
  const [showAdd, setShowAdd] = useState(false);
  const [shelterName, setShelterName] = useState('');
  const [district, setDistrict] = useState('');
  const [startDate, setStartDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [endDateFor, setEndDateFor] = useState<string | null>(null);
  const [endDate, setEndDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const start = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m27/dv-cases/${dvSupportCaseId}/shelter-placements`, { shelterName, district, startDate });
      setShelterName('');
      setDistrict('');
      setShowAdd(false);
      onReload();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not start shelter placement');
    } finally {
      setBusy(false);
    }
  };

  const end = async (placementId: string) => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m27/dv-cases/${dvSupportCaseId}/shelter-placements/${placementId}/end`, { endDate });
      setEndDateFor(null);
      onReload();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not end placement');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-sm font-bold text-slate-700">Shelter placements</h3>
        <button className="btn-secondary !px-2.5 !py-1 text-xs" onClick={() => setShowAdd((v) => !v)}>
          {showAdd ? 'Cancel' : '+ Start placement'}
        </button>
      </div>
      {error ? (
        <div className="mb-2">
          <ErrorNote message={error} />
        </div>
      ) : null}
      {placements.length === 0 ? (
        <EmptyState title="No shelter placements recorded" />
      ) : (
        <ul className="mb-3 space-y-2">
          {placements.map((p) => (
            <li key={p.id} className="rounded-lg border border-slate-200 p-3 text-sm">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium">{p.shelterName}</div>
                  <div className="text-xs text-slate-500">
                    {p.district.replaceAll('-', ' ')} · from {formatDate(p.startDate)}
                    {p.endDate ? ` to ${formatDate(p.endDate)}` : ''}
                  </div>
                </div>
                <StatusBadge status={p.status} />
              </div>
              {p.status === 'ACTIVE' ? (
                endDateFor === p.id ? (
                  <div className="mt-2 flex items-center gap-2">
                    <input className="field-input !py-1 text-xs" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                    <button className="btn-secondary !px-2 !py-1 text-xs" onClick={() => end(p.id)} disabled={busy}>
                      Confirm end
                    </button>
                  </div>
                ) : (
                  <button className="btn-secondary mt-2 !px-2 !py-0.5 text-xs" onClick={() => setEndDateFor(p.id)}>
                    End placement
                  </button>
                )
              ) : null}
            </li>
          ))}
        </ul>
      )}
      {showAdd ? (
        <div className="space-y-2 rounded-lg border border-slate-200 p-3">
          <Field label="Shelter name">
            <input className="field-input" value={shelterName} onChange={(e) => setShelterName(e.target.value)} placeholder="undisclosed address kept out of this field's label only" />
          </Field>
          <div className="grid grid-cols-2 gap-2">
            <Field label="District (slug)">
              <input className="field-input" value={district} onChange={(e) => setDistrict(e.target.value)} placeholder="anse-royale" />
            </Field>
            <Field label="Start date">
              <input className="field-input" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </Field>
          </div>
          <div className="flex justify-end">
            <button className="btn-primary !py-1 text-xs" onClick={start} disabled={busy || !shelterName || !district}>
              {busy ? 'Saving…' : 'Start placement'}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export { openOrCreateDvSupportCase };
